import {
  Instagram,
  Linkedin,
  Phone,
  Twitter,
  MapPin,
  Mail,
  Clock,
  Facebook,
  ChevronDown,
  X,
} from "lucide-react";
import React, { useEffect, useState, useCallback, useTransition, useDeferredValue, useRef } from "react";
import { Grid } from "../../ui/Grid";
import helpers from "../../../lib/crypto";
import Joi from "joi";
import axios from "axios";
import confetti from "canvas-confetti";
import { useFunctionContext } from "../../context/FunctionalContext";
import countryList from "../../../utils/data";

const officeData = {
  address: "Office 805, Donna Towers,",
  address2: "Dubai Silicon Oasis, Dubai, UAE",
  phone: "+971 52 794 7540",
  landline: "+971 4 557 9914",
  email: "admin@scanglobal.me",
  hours: [
    "Monday - Friday: 9:00 AM - 6:00 PM",
    "Saturday: 10:00 AM - 4:00 PM",
    "Sunday: Closed",
  ],
};

const API_URL = import.meta.env.VITE_API_URL;

const services = [
  "Business Advisory",
  "Finance and Accounting",
  "Real Estate Services",
  "Other",
];

const referredBy = [
  "Wahaj",
  "Rabia",
  "Lysann",
  "Maricel",
  "Bhuvan",
  "Jennifer",
  "Nikola",
  "Other",
];

const Dropdown = React.memo(({ label, options, value, setValue }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-4 py-3 focus:ring-1 focus:ring-gold placeholder-gray-500 transition-colors duration-200 bg-slate-800/60 border border-gold/10 rounded-md focus:outline-none focus:border-gold caret-gold"
      >
        <span className={value ? "text-white" : "text-gray-500"}>
          {value || `Select ${label}`}
        </span>
        <span className="text-gray-400">▾</span>
      </button>

      {open && (
        <ul
          className="absolute z-20 mt-2 max-h-52 overflow-y-auto w-full rounded-xl bg-slate-800 shadow-lg border border-gray-600 overflow-hidden"
        >
          {options.map((option) => (
            <li
              key={option}
              onClick={() => {
                setValue(option);
                setOpen(false);
              }}
              className="px-4 py-3 hover:bg-slate-700 cursor-pointer text-sm"
            >
              {option}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

Dropdown.displayName = "Dropdown";

const Contact = () => {
  const { ORG_ID } = useFunctionContext();

  const [loading, setLoading] = useState(false);
  const [selectedCode, setSelectedCode] = useState("+91");
  const [selectedEmoji, setSelectedEmoji] = useState("🇮🇳");
  const [phoneNumberModalOpen, setPhoneNumberModalOpen] = useState(false);
  const searchInputRef = useRef(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [isPending, startTransition] = useTransition();
  const [visibleCount, setVisibleCount] = useState(30);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    services: "",
    referredBy: "",
    message: "",
  });

  const [errors, setErrors] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleChange = useCallback(({ currentTarget: inp }) => {
    const { name, value } = inp;

    // Immediately update for responsive typing
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Defer the expensive validation
    startTransition(() => {
      let newValue = value;

      if (name === "message") {
        newValue = value.replace(/[^\w\s.\-/:?,]/gi, "");
      }

      if (name === "name") {
        newValue = value.replace(/[^a-zA-Z0 ]/gi, "");
      }

      if (name === "email") {
        newValue = value.replace(/[^a-zA-Z0-9@. ]/gi, "");
      }

      // Only update if sanitization changed the value
      if (newValue !== value) {
        setFormData((prev) => ({
          ...prev,
          [name]: newValue,
        }));
      }
    });
  }, []);

  const handlePhoneChange = ({ currentTarget: inp }) => {
    const { value } = inp;
    const newValue = value.replace(/[^0-9]/g, "");
    setFormData((prev) => ({ ...prev, phoneNumber: newValue }));
  };

  const handleServicesChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, services: value }));
  }, []);

  const handleReferredByChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, referredBy: value }));
  }, []);

  const add_leads = (data) => {
    const schema = Joi.object({
      key: Joi.string().required(),
      source: Joi.string().required(),
      lead_name: Joi.string().trim().min(2).max(30).required(),
      email: Joi.string()
        .trim()
        .min(10)
        .max(55)
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.email": "Please enter a valid email address",
        }),
      phoneNumber: Joi.string().min(6).max(15).required(),
      services: Joi.string().min(3).max(100).required(),
      referredBy: Joi.string().min(3).max(100).required(),
      admin_type: Joi.string().optional().allow("", null),
      organisation_id: Joi.string().required(),
      comments: Joi.string().optional().allow("", null),
      route_action: Joi.number().valid(1).required(),
      status: Joi.string().trim().valid("New").required(),
      next_follow_up: Joi.date().iso().allow("", null),
    });

    return schema.validate(data);
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log("form submitted");

    if (!formData?.email || !formData?.name || !formData?.message || !formData?.phoneNumber || !formData?.services || !formData?.referredBy) {
      setErrors("Please fill all the fields");
      setLoading(false);
      return;
    }

    const payload = {
      key: "scanglobal",
      source: "Realty",
      lead_name: formData.name,
      email: formData.email,
      comments: formData.message || "",
      phoneNumber: selectedCode.replace("+", "") + formData.phoneNumber,
      services: formData.services,
      referredBy: formData.referredBy,
      route_action: 1,
      status: "New",
      next_follow_up: new Date().toISOString(),
      admin_type: "1",
      organisation_id: ORG_ID,
    };

    const { error } = add_leads(payload);

    if (error) {
      console.log("error is present");
      setErrors(error.details[0].message);
      setLoading(false);
      return;
    }

    let encrypted;
    try {
      encrypted = helpers.encryptobj(payload);
      console.log("📦 Encrypted Payload to Send:", encrypted);
    } catch (e) {
      // console.error("❌ Encryption error:", e);
      setErrors("Encryption failed.");
      setLoading(false);
      return;
    }

    try {
      console.log("🚀 Initiating Axios POST request...");
      const response = await axios.post(
        `${API_URL}/admin/add_update_lead`,
        { enc: encrypted },
        { headers: { "Content-Type": "application/json" } }
      );

      // console.log("✅ Axios Response:", response.data);

      if (response.status === 200) {
        setSuccessMessage("Your message has been sent successfully!");
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        setErrors("");
        setFormData({
          name: "",
          email: "",
          phoneNumber: "",
          services: "",
          referredBy: "",
          message: "",
        });
      } else {
        setErrors(
          response.data?.message || "Something went wrong on the server"
        );
      }
    } catch (err) {
      console.error("❌ Axios Submit Error:", err);
      if (err.response) {
        console.error("💡 Server Response:", err.response.data);
      }
      setErrors("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Detect country based on IP
    const detectCountry = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        if (data.country_code) {
          const country = countryList.find(c => c.code === data.country_code);
          if (country) {
            setSelectedCode(country.dial_code);
            setSelectedEmoji(country.emoji);
            console.log(`🌍 Auto-detected country via IP: ${country.name} (${country.dial_code})`);
            return;
          }
        }
      } catch (error) {
        console.log('IP detection failed, trying browser geolocation...');
      }

      // Fallback to browser geolocation
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
              );
              const data = await response.json();

              if (data.address && data.address.country_code) {
                const countryCode = data.address.country_code.toUpperCase();
                const country = countryList.find(c => c.code === countryCode);
                if (country) {
                  setSelectedCode(country.dial_code);
                  setSelectedEmoji(country.emoji);
                  console.log(`📍 Auto-detected country via GPS: ${country.name} (${country.dial_code})`);
                }
              }
            } catch (error) {
              console.log('Geolocation reverse geocoding failed, using default');
            }
          },
          (error) => {
            console.log('Geolocation permission denied or unavailable, using default');
          }
        );
      }
    };

    detectCountry();
  }, []);

  const filteredCountries = React.useMemo(() => {
    return countryList.filter(
      (c) =>
        c.name.toLowerCase().includes(deferredSearch.toLowerCase()) ||
        c.dial_code.includes(deferredSearch)
    );
  }, [deferredSearch]);

  useEffect(() => {
    setVisibleCount(30);
  }, [deferredSearch]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <>
      <section className="relative w-full h-[65vh] md:h-[85vh] bg-gradient-to-r from-gold/10 to-black text-white py-10 sm:py-16 md:pt-32 lg:pt-36 lg:pb-24 ">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{
            backgroundImage: "url('/images/realty/19.jpeg')",
            backgroundBlendMode: "overlay",
          }}
        />
        <div className="relative container mx-auto px-4 py-16 md:py-24 lg:py-32 flex flex-col items-center text-center mt-28 md:mt-10 z-10">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in">
            Let's Talk Real Estate
          </h1>
          <p className="text-lg md:text-xl max-w-2xl opacity-90 animate-slide-up">
            We're here to help you with your property needs.
          </p>
        </div>
      </section>
      {/* Contact Us */}
      <section className="py-10 lg:py-24 md:py-16 lg:pt-32 lg:pb-32 container">
        <div className="relative z-10">
          <div className="flex flex-col md:flex-row border border-slate-600">
            {/* Left side - Light */}
            <div className="w-full md:w-1/2 flex bg-slate-300 p-8 md:p-16 relative overflow-hidden">
              <div className="absolute inset-0 bg-slate-900/30 bg-opacity-90"></div>
              <div className="relative z-10 my-auto">
                {/* <Logo /> */}

                <div className="overflow-hidden">
                  <h1 className="text-3xl md:text-5xl font-black mb-8">
                    <Grid size={20} />
                    Contact
                    {/* <span className="relative">
                      .
                      <span className="absolute -bottom-2 -right-1 w-16 h-8 bg-gold -z-10"></span>
                    </span> */}
                  </h1>

                  <p className="text-gray-300 max-w-sm mb-8">
                    At ScanGlobal, we value every message. Whether it's a
                    business inquiry or a service request, your message won’t go
                    unnoticed. Reach out to us, and a dedicated team member will
                    respond promptly. Name and Email Address to respond. No
                    spam—just the information you ask for.
                  </p>

                  <p className="text-sm text-gray-300">
                    We only need your{" "}
                    <span className="font-semibold">Name</span> and{" "}
                    <span className="font-semibold">Email Address</span> to
                    respond. No spam—just the information you ask for.
                  </p>
                </div>

                <div className="mt-8">
                  <div className="flex space-x-2">
                    {/* <a
                      href="#"
                      className="p-2 bg-gray-500 hover:bg-gray-600 transition-colors duration-300 text-white rounded-sm"
                    >
                      <Twitter size={16} />
                    </a> */}
                    <a
                      href="https://www.linkedin.com/company/scanglobalrealty/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-500 hover:bg-gray-600 transition-colors duration-300 text-white rounded-sm"
                    >
                      <Linkedin size={16} />
                    </a>
                    <a
                      href="https://www.instagram.com/scanglobalrealty?igsh=djk0ZHYxbXIwaWdm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-500 hover:bg-gray-600 transition-colors duration-300 text-white rounded-sm"
                    >
                      <Instagram size={16} />
                    </a>
                    <a
                      href="https://www.facebook.com/ScanGlobalRealty"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-gray-500 hover:bg-gray-600 transition-colors duration-300 text-white rounded-sm"
                    >
                      <Facebook size={16} />
                    </a>
                  </div>
                </div>
                {/* <div className="absolute top-40 right-36 z-10 transform rotate-12">
                  <div className="w-4 h-4 bg-gold"></div>
                </div> */}

                {/* Squiggly line */}
                {/* <div className="absolute bottom-32 right-20 z-10">
                  <svg width="100" height="30" viewBox="0 0 100 30" fill="none">
                    <path
                      d="M0,15 C10,5 20,25 30,15 C40,5 50,25 60,15 C70,5 80,25 90,15 C100,5 110,25 120,15"
                      stroke="white"
                      strokeWidth="1"
                      fill="none"
                    />
                  </svg>
                </div> */}

                {/* Bottom decorative line */}
                {/* <div className="absolute bottom-28 left-0 w-full h-1 bg-gold"></div> */}

                {/* Scribble */}
                {/* <div className="absolute -bottom-9 left-24">
                  <svg width="60" height="40" viewBox="0 0 60 40" fill="none">
                    <path
                      d="M5,20 C15,25 20,15 30,20 C40,25 45,15 55,20"
                      stroke="#fff"
                      strokeWidth="3"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray="1,3"
                    />
                  </svg>
                </div> */}
              </div>
            </div>

            {/* Right side - Dark */}
            <div className="w-full md:w-1/2 bg-black p-8 lg:p-16 flex flex-col bg-[url('/images/realty/18.jpg')] bg-cover bg-center overflow-hidden">
              <div className="absolute inset-0 bg-black bg-opacity-90"></div>
              <div className="relative z-10 flex flex-col h-full">
                <div className="flex-grow flex flex-col justify-center">
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-white mb-8">
                      Send Me A Message
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder=" Name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full bg-transparent border border-gray-700 rounded p-3 text-white focus:outline-none focus:border-gold transition-colors duration-300"
                            required
                            maxLength={25}
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full bg-transparent border border-gray-700 rounded p-3 text-white focus:outline-none focus:border-gold transition-colors duration-300"
                            required
                            maxLength={25}
                          />
                        </div>
                      </div>

                      {/* Phone Number */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <div className="flex items-center w-full px-3 py-3 bg-transparent border border-gray-700 rounded focus-within:border-gold transition">
                            {/* Country selector */}
                            <div className="relative">
                              <button
                                type="button"
                                className="flex items-center gap-1 text-sm text-gray-300 pr-2"
                                onClick={() => {
                                  setPhoneNumberModalOpen(!phoneNumberModalOpen);
                                  setVisibleCount(30);
                                }}
                              >
                                <span>{selectedEmoji}</span>
                                <span>{selectedCode}</span>
                                <ChevronDown className="size-3" />
                                <span className="mx-1 text-gray-500">|</span>
                              </button>

                              {phoneNumberModalOpen && (
                                <div
                                  className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                                  onClick={() => setPhoneNumberModalOpen(false)}
                                >
                                  <div
                                    className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-900">
                                      <h3 className="text-white font-semibold text-lg">
                                        Select Country
                                      </h3>
                                      <button
                                        type="button"
                                        onClick={() => setPhoneNumberModalOpen(false)}
                                        className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-slate-800 rounded-full"
                                      >
                                        <X className="size-6" />
                                      </button>
                                    </div>
                                    <div className="p-3 bg-slate-900 border-b border-slate-700">
                                      <input
                                        ref={searchInputRef}
                                        autoFocus
                                        type="text"
                                        placeholder="Search country..."
                                        defaultValue=""
                                        onChange={(e) => {
                                          const value = e.target.value;
                                          startTransition(() => {
                                            setSearch(value);
                                          });
                                        }}
                                        className="w-full px-4 py-2.5 text-base bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-gold text-white placeholder-gray-500"
                                      />
                                    </div>

                                    <div
                                      className="max-h-[50vh] overflow-y-auto"
                                      onScroll={(e) => {
                                        const { scrollTop, scrollHeight, clientHeight } = e.target;
                                        if (scrollHeight - scrollTop <= clientHeight * 1.5) {
                                          setVisibleCount(prev => Math.min(prev + 20, filteredCountries.length));
                                        }
                                      }}
                                    >
                                      {filteredCountries
                                        .slice(0, visibleCount)
                                        .map((c) => (
                                          <div
                                            key={c.code}
                                            className="px-4 py-3 flex justify-between items-center hover:bg-slate-800 cursor-pointer border-b border-slate-800/50 last:border-0 transition-colors"
                                            onClick={() => {
                                              setSelectedCode(c.dial_code);
                                              setSelectedEmoji(c.emoji);
                                              setPhoneNumberModalOpen(false);
                                              setSearch("");
                                            }}
                                          >
                                            <span className="flex items-center gap-3 text-base">
                                              <span className="text-2xl">{c.emoji}</span>{" "}
                                              <span className="text-gray-200 font-medium">
                                                {c.name}
                                              </span>
                                            </span>
                                            <span className="text-gray-400 font-medium bg-slate-800 px-2 py-1 rounded text-sm">
                                              {c.dial_code}
                                            </span>
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Phone input */}
                            <input
                              type="tel"
                              inputMode="numeric"
                              placeholder="Enter contact number"
                              value={formData.phoneNumber}
                              onChange={handlePhoneChange}
                              className="flex-1 bg-transparent text-sm focus:outline-none caret-gold text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Services */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <Dropdown
                            label="Services"
                            options={services}
                            value={formData.services}
                            setValue={handleServicesChange}
                          />
                        </div>
                      </div>

                      {/* Referred By */}
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <Dropdown
                            label="Referred By"
                            options={referredBy}
                            value={formData.referredBy}
                            setValue={handleReferredByChange}
                          />
                        </div>
                      </div>

                      <div>
                        <textarea
                          name="message"
                          placeholder="Message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={5}
                          className="w-full bg-transparent border border-gray-700 rounded p-3 text-white focus:outline-none focus:border-gold transition-colors duration-300 resize-none"
                          required
                        ></textarea>
                      </div>

                      <div className="mt-6">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`bg-gold text-slate-900 font-semibold px-6 py-3 rounded transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-opacity-50 flex items-center justify-center gap-2 ${loading
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-gold"
                            }`}
                        >
                          {loading ? (
                            <>
                              <span className="h-4 w-4 border-2 border-black border-t-transparent rounded-full animate-spin"></span>
                              Sending...
                            </>
                          ) : (
                            "SEND MESSAGE"
                          )}
                        </button>

                        {/* Feedback messages */}
                        {errors && (
                          <p className="mt-4 text-sm text-red-400 font-medium text-start">
                            {errors}
                          </p>
                        )}

                        {successMessage && (
                          <p className="mt-4 text-base text-green-400 font-medium text-start">
                            {successMessage}
                          </p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* End Contact Us */}
      </section>
      {/* Office Information */}
      <section className="py-10 md:pb-16 lg:pb-32 container mx-auto">
        <div className="">
          <h2 className="text-2xl font-semibold mb-6">Office Information</h2>
          <div className="bg-slate-900/70 rounded-lg shadow-md p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-">
              <div className="flex items-start hover:scale-105 transition-transform duration-300 hover:bg-slate-800/40 p-4 rounded-lg">
                <div className="flex-shrink-0 mt-1">
                  <MapPin className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-500">Address</h3>
                  {/* <p className="mt-1 text-gray-300 text-lg font-semibold">
                    {officeData.landmark}
                  </p> */}
                  <p className="mt-1 text-gray-300 text-base">
                    {officeData.address}
                  </p>
                  <p className="mt-1 text-gray-300 text-base">
                    {officeData.address2}
                  </p>
                </div>
              </div>

              <div className="flex items-start hover:scale-105 transition-transform duration-300 hover:bg-slate-800/40 p-4 rounded-lg">
                <div className="flex-shrink-0 mt-1">
                  <Phone className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-500">Phone</h3>
                  <p className="mt-1 text-gray-300">{officeData.phone}</p>
                  <p className="mt-1 text-gray-300">{officeData.landline}</p>
                </div>
              </div>

              <div className="flex items-start hover:scale-105 transition-transform duration-300 hover:bg-slate-800/40 p-4 rounded-lg">
                <div className="flex-shrink-0 mt-1">
                  <Mail className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-500">Email</h3>
                  <p className="mt-1 text-gray-300">{officeData.email}</p>
                </div>
              </div>

              <div className="flex items-start hover:scale-105 transition-transform duration-300 hover:bg-slate-800/40 p-4 rounded-lg">
                <div className="flex-shrink-0 mt-1">
                  <Clock className="h-6 w-6 text-gold" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-500">
                    Working Hours
                  </h3>
                  <ul className="mt-1 text-gray-400 space-y-1">
                    {officeData.hours.map((hour, index) => (
                      <li key={index}>{hour}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-8 rounded-lg overflow-hidden shadow-md h-48 md:h-96">
              <div className="w-full h-full relative">
                <img
                  src="/images/realty/17.webp"
                  alt="Office Location"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-blue-900 bg-opacity-10 flex items-center justify-center">
                  <span className="bg-white px-4 py-2 rounded-md shadow-md text-blue-800 font-medium">
                    Visit Us
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
